import React from 'react'
import ReactECharts from 'echarts-for-react'
import type { KeyStatSurveyItem } from '../../../../models/Report'
import { useReportsContext } from '../../ReportsContext'
import { getMaxLabelLength, truncateLabel } from '../helper'

const barColors = [
  'rgb(66, 133, 244)',
  'rgb(219, 68, 55)',
  'rgb(244, 180, 0)',
  'rgb(15, 157, 88)',
  'rgb(171, 71, 188)',
  'rgb(0, 172, 193)',
  'rgb(255, 112, 67)',
  'rgb(158, 157, 36)'
]

interface StackedChartForMultiChoiceAndMultiSelectProps {
  titles?: string[]
  report: KeyStatSurveyItem
}

const StackedChartForMultiChoiceAndMultiSelect: React.FC<StackedChartForMultiChoiceAndMultiSelectProps> = ({
  titles,
  report
}) => {
  const numTerms = report.options?.[0]?.percents?.length ?? 0
  const xLabels = titles && titles.length === numTerms
    ? titles
    : Array.from({ length: numTerms }, (_, i) => `Term ${i + 1}`)

  const { isFullScreen } = useReportsContext()
  const MAX_LABEL_TERMS = 5
  const MAX_LABEL_LENGTH = getMaxLabelLength(isFullScreen, xLabels.length)

  // Build stacked bar chart data from report.options[].percents
  const showLabels = xLabels.length <= MAX_LABEL_TERMS
  const chartData = (report.options || []).map((opt: any, idx: number) => ({
    name: opt.name,
    type: 'bar',
    stack: 'total',
    emphasis: { focus: 'series', label: { show: false } },
    label: {
      show: showLabels,
      position: 'inside',
      fontWeight: 600,
      formatter: (params: any) => {
        if (params.value > 0) {
          const val = Number(params.value)
          const valStr = val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)
          const label = truncateLabel(String(opt.name), MAX_LABEL_LENGTH)
          return `${label} (${valStr}%)`
        }
        return ''
      },
      fontSize: 14
    },
    itemStyle: {
      color: barColors[idx % barColors.length]
    },
    data: (opt.percents || []).map((v: number) => v * 100)
  }))

  const option = {
    grid: { left: 20, right: 20, bottom: 40, top: 30 },
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      showContent: true,
      enterable: true,
      alwaysShowContent: false,
      triggerOn: 'mousemove|click',
      confine: true,
      renderMode: 'html',
      extraCssText: 'pointer-events:auto;',
      formatter: (params: any) => {
        if (Array.isArray(params) && params.length > 0) {
          const axisLabel = params[0].axisValueLabel || params[0].name || ''
          const lines = [axisLabel]
          params.forEach((item: any) => {
            if (item.value > 0) {
              const val = Number(item.value)
              const valStr = val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)
              lines.push(`${item.marker}${item.seriesName}: ${valStr}%`)
            }
          })
          return lines.join('<br/>')
        }
        return ''
      }
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      nameLocation: 'middle',
      nameGap: 30,
      axisLine: { show: false },
      axisLabel: { fontWeight: 'bold', margin: 20 },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      show: true,
      splitLine: { show: true },
      axisLabel: { show: false }
    },
    series: chartData.map(s => ({ ...s, emphasis: { focus: 'none' } }))
  }

  return (
    <ReactECharts
      lazyUpdate
      style={{ width: '100%', height: '400px', maxHeight: 500 }}
      option={option}
    />

  )
}

export default StackedChartForMultiChoiceAndMultiSelect
